import { FC, useEffect, useState } from 'react';
import { Section, Cell, Button, Caption, Spinner, Badge, Accordion } from '@telegram-apps/telegram-ui';
import { TbChevronRight, TbPlus, TbSnowflake } from 'react-icons/tb';
import { useNavigation } from '@/providers/NavigationProvider.tsx';
import { useTranslation } from 'react-i18next';
import { getSecretSantaGames } from '@/api/apiMethods.ts';
import { SecretSantaGame } from '@/api/types.ts';
import { useAuthUser } from '@/providers';
import {haptic} from "@/helpers/haptic.ts";

export const SecretSantaGamesList: FC<{ inSeason: boolean }> = ({ inSeason }) => {
  const { navigateToRoute, showSnackbar } = useNavigation();
  const { t, i18n: { language } } = useTranslation();
  const { authUser } = useAuthUser();
  const [games, setGames] = useState<SecretSantaGame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);


  useEffect(() => {
    let cancelled = false;
    const fetchGames = async () => {
      try {
        setIsLoading(true);
        const fetchedGames = await getSecretSantaGames();
        if (!cancelled) setGames(fetchedGames);
      } catch (error) {
        if (cancelled) return;
        console.error(error)
        haptic.notification('error');
        showSnackbar("❌", "secretSanta.error.loadFailed");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchGames().then();
    return () => { cancelled = true; };
  }, []);

  const myGames = games.filter(game => game.organizer_id === authUser?.id);
  const participatingGames = games.filter(game => 
    game.organizer_id !== authUser?.id &&
    game.participants.some(p => p.user_id === authUser?.id)
  );

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    return date.toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusText = (status: string) => {
    return t(`secretSanta.status.${status}`);
  };

  const sectionTitle = (
    <span className="inline-flex items-center gap-2">
      {inSeason && <TbSnowflake size={16} className="tg-link-color" aria-hidden="true" />}
      {t('secretSanta.title')}
      {inSeason && <Badge type="number" mode="secondary">Seasonal</Badge>}
    </span>
  );
  const sectionHeader = <Section.Header>{sectionTitle}</Section.Header>;
  const loadingContent = (
    <div className="flex justify-center p-5">
      <Spinner size="s" />
    </div>
  );

  const gamesContent = (
    <>

        <Section header={inSeason ? sectionHeader : undefined}>
            {myGames.map((game) => (
              <Cell
                key={game.id}
                description={
                  <div className="flex items-center gap-2">
                    {game.event_date && <Caption>{formatDate(game.event_date)}</Caption>}
                    <Caption>•</Caption>
                    <Caption>{getStatusText(game.status)}</Caption>
                  </div>
                }
                after={
                  <Caption weight="2" className="flex gap-1 items-center tg-link-color">
                    {t('secretSanta.participants.total', { count: game.participants.length })}
                    <TbChevronRight size={20} />
                  </Caption>
                }
                onClick={() => {
                  haptic.impact('light');
                  navigateToRoute(`/main/secret-santa/${game.id}`);
                }}
              >
                {game.title}
              </Cell>
            ))}
            <Button
              stretched={true}
              className="mb-1"
              onClick={() => {
                haptic.impact('light');
                navigateToRoute('/main/secret-santa/new');
              }}
              before={<TbPlus size={22} />}
              mode="plain"
            >
              {t('secretSanta.create')}
            </Button>
        </Section>

        {participatingGames.length > 0 && (
          <Section header={t('secretSanta.participant')}>
            {participatingGames.map((game) => (
              <Cell
                key={game.id}
                description={
                  <div className="flex items-center gap-2">
                    {game.event_date && <Caption>{formatDate(game.event_date)}</Caption>}
                    <Caption>•</Caption>
                    <Caption>{getStatusText(game.status)}</Caption>
                  </div>
                }
                after={
                  <Caption weight="2" className="flex gap-1 items-center tg-link-color">
                    {t('secretSanta.participants.total', { count: game.participants.length })}
                    <TbChevronRight size={20} />
                  </Caption>
                }
                onClick={() => {
                  haptic.impact('light');
                  navigateToRoute(`/main/secret-santa/${game.id}`);
                }}
              >
                {game.title}
              </Cell>
            ))}
          </Section>
        )}

    </>
  );

  if (inSeason) {
    return isLoading
      ? <Section header={sectionHeader}>{loadingContent}</Section>
      : gamesContent;
  }

  return (
    <Section>
      <Accordion expanded={isExpanded} onChange={(expanded) => {
        haptic.selection();
        setIsExpanded(expanded);
      }}>
        <Accordion.Summary>{sectionTitle}</Accordion.Summary>
        <Accordion.Content style={{ background: 'var(--tgui--section_bg_color)' }}>
          <div className="flex flex-col gap-3">
            {isLoading ? loadingContent : gamesContent}
          </div>
        </Accordion.Content>
      </Accordion>
    </Section>
  );
};
